import { Input } from 'antd';
import { TextAreaProps } from 'antd/es/input';
import clsx from 'clsx';
import React, { HTMLAttributes } from 'react';
import { Control, Controller, FieldValues, Path } from 'react-hook-form';

type AppTextAreaProps<T extends FieldValues> = TextAreaProps & {
  required?: boolean;
  label?: string;
  name: Path<T>;
  placeholder?: string;
  control: Control<T>;
  suffixLabel?: React.JSX.Element;
  labelProps?: HTMLAttributes<HTMLDivElement>;
};

type LabelProps = HTMLAttributes<HTMLParagraphElement> & {
  suffixLabel?: React.JSX.Element;
  required?: boolean;
};

export const FormFieldGroup = (props: HTMLAttributes<HTMLDivElement>) => {
  const { children, className, ...restProps } = props;
  return (
    <div className={clsx('flex flex-col gap-1.5 w-full', className)} {...restProps}>
      {children}
    </div>
  );
};

export const Label = (props: LabelProps) => {
  const { children, className, suffixLabel, required, ...restProps } = props;
  return (
    <p className={clsx('text-base leading-5 font-medium text-text-800 capitalize!', className)} {...restProps}>
      {children} {suffixLabel && <span className="text-text-400 text-xs font-normal">{suffixLabel}</span>}
      {required && <span className="text-red-500">*</span>}
    </p>
  );
};

type FormFieldErrorProps = HTMLAttributes<HTMLParagraphElement> & {
  message?: string | null;
};

export const FormFieldError = (props: FormFieldErrorProps) => {
  const { ...restProps } = props;
  if (props?.message) {
    return (
      <p className="text-sm font-normal text-red-500!" {...restProps}>
        {props.message}
      </p>
    );
  }
};

export function AppTextArea<T extends FieldValues>(props: AppTextAreaProps<T>) {
  const { required, label, name, placeholder, suffixLabel, control, labelProps, className, ...restProps } = props;
  const { TextArea } = Input;

  return (
    <FormFieldGroup>
      <Label suffixLabel={suffixLabel} required={required} {...labelProps}>
        {label}
      </Label>

      <Controller
        name={name}
        control={control}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <div className="w-full flex flex-col gap-1.5">
            <TextArea
              size="large"
              status={error && 'error'}
              title={label}
              value={value}
              onChange={onChange}
              placeholder={placeholder}
              className={clsx('leading-5!', className)}
              {...restProps}
            />
            <FormFieldError message={error?.message} />
          </div>
        )}
      />
    </FormFieldGroup>
  );
}
